/* eslint-disable max-statements */
/* eslint-disable eqeqeq */
/* eslint-disable no-eq-null */

// import { useGesture } from '@use-gesture/react';
// import { animated, useSpring } from '@react-spring/web';

import { useContext, useMemo, useRef } from 'react';

import { Components, IslandType, Vertex2, Vertex3 } from '@rta/rta-blueprint-library';

import { addContextMenu, ReferenceEntryType } from '../../context-menu';
import { AppContext } from '@r-app/app-context';
import { DisplayMode } from '@r-core';
import { getDimensionMeasurement } from '@r-core/helpers';
import { useDraggableSvg } from '@r-services/ui-interactions';
import { useTextWidth } from '@r-hooks';
import {
	DIMENSION_CENTER_LINE_INITIAL_Y,
	DIMENSION_CENTER_LINE_OVAL_STROKE_WIDTH,
	DIMENSION_CENTER_LINE_PADDING_TOP,
	DIMENSION_FONT_SIZE,
	DIMENSION_STROKE_WIDTH,
	SYMBOL_ARROW_SIZE
} from '@r-core/constants';

export type DrawApplianceGroupCenterDimensionProps = {
	panelItemGroupId: string;
	isCountertop?: boolean;
	manufacturingView?: boolean;
};

export const DrawApplianceGroupCenterDimension = ({ panelItemGroupId, isCountertop = false }: DrawApplianceGroupCenterDimensionProps): React.ReactElement => {
	const { controller } = useContext(AppContext);
	// The group can be gone (last item deleted) when a subscription re-renders; getReferenceMap returns null for a removed id.
	if (!controller.kitchen.getReferenceMap(panelItemGroupId)) return <></>;
	return <DrawApplianceGroupCenterDimensionContent panelItemGroupId={panelItemGroupId} isCountertop={isCountertop} />;
};

const DrawApplianceGroupCenterDimensionContent = ({ panelItemGroupId, isCountertop = false }: DrawApplianceGroupCenterDimensionProps): React.ReactElement => {
	const {
		controller: { kitchen, options, navigation }
	} = useContext(AppContext);
	const { island, panel, panelItemGroup } = kitchen.getReferenceMap(panelItemGroupId);
	const movableRef = useRef(null);
	const prevGroup = useMemo(() => panelItemGroup.getPreviousGroup(), []);
	const nextGroup = useMemo(() => panelItemGroup.getNextGroup(), []);

	const hasTopOnlyAppliance = panelItemGroup.items.children.some((panelItem) => panelItem.item.placement === Components.PLACEMENT_TOP_ONLY) ?? false;
	let { showAppliances, showCutouts } = options;
	switch (navigation.displayMode) {
		case DisplayMode.Manufacturing:
		case DisplayMode.InlineManufacturing:
			showAppliances = false;
			showCutouts = true;
			break;
		default:
	}

	const halfArrow = SYMBOL_ARROW_SIZE / 2;
	const fontSize = DIMENSION_FONT_SIZE * options.dimensions.fontSizeFactor;

	const centerpoint = new Vertex3({
		dataIn: {
			x: panel.panelItemGroups ? panelItemGroup.center.x : 0,
			y: DIMENSION_CENTER_LINE_INITIAL_Y + island.islandSize.height - (fontSize * 0.85),
			z: 0
		}
	});

	const boundary = {
		// permit appliance groups to cross over each other
		left: panel.size.width / -2,
		right: panel.size.width / 2
	};

	useDraggableSvg({
		ref: movableRef,
		enabled: [!panelItemGroup.positionLocked, false],
		position: { x: panelItemGroup.center.x, y: 0 },
		bBox: {
			origin: new Vertex2(),
			size: {
				width: boundary.right - boundary.left,
				height: panel.size.height,
				depth: 0
			},
			padding: [panelItemGroup.maxOutsideWidth / 2, 0, panelItemGroup.maxOutsideWidth / 2, 0]
		},
		onDragging: ({ position: p }) => {
			if (panelItemGroup.positionLocked) {
				return false;
			}
			// panelItemGroup.center.x = p.new.x;
			panelItemGroup.move({ x: p.new.x }, hasTopOnlyAppliance);
			return true;
		},
		onDragEnd: ({ position: p }) => {
			if (panelItemGroup.positionLocked) {
				return false;
			}
			// enable 'snap to' feature
			// const itemRange = [p.new.x - (panelItemGroup.maxOutsideWidth / 2), p.new.x + (panelItemGroup.maxOutsideWidth / 2)];
			// const prevGroupRange = prevGroup ? [prevGroup.center.x - (prevGroup.maxOutsideWidth / 2), prevGroup.center.x + (prevGroup.maxOutsideWidth / 2)] : null;
			// const nextGroupRange = nextGroup ? [nextGroup.center.x - (nextGroup.maxOutsideWidth / 2), nextGroup.center.x + (nextGroup.maxOutsideWidth / 2)] : null;

			// // chcek if position overlaps with previous group
			// if (prevGroupRange && itemRange[0] < prevGroupRange[1] && itemRange[1] > prevGroupRange[0]) {
			// 	// snap position based on left or right overlap
			// 	p.new.x = p.new.x < prevGroup.center.x
			// 			? prevGroupRange[0] - (panelItemGroup.maxOutsideWidth / 2)
			// 			: prevGroupRange[1] + (panelItemGroup.maxOutsideWidth / 2);
			// }

			// // check if position overlaps with next group
			// if (nextGroupRange && itemRange[0] < nextGroupRange[1] && itemRange[1] > nextGroupRange[0]) {
			// 	// snap position based on left or right overlap
			// 	p.new.x = p.new.x < nextGroup.center.x
			// 			? nextGroupRange[0] - (panelItemGroup.maxOutsideWidth / 2)
			// 			: nextGroupRange[1] + (panelItemGroup.maxOutsideWidth / 2);
			// }

			return true;
		}
	});

	const distanceFrontFace = !showAppliances && showCutouts ? 0 : panelItemGroup.items.children[0]?.item.distanceFrontOfFace;

	const centerOffset = isCountertop ? island.panels.findById(panel.connections.left.targetPanelId).countertopOverhang : 0;
	const centerText = `\u2104 ${getDimensionMeasurement(centerpoint.x + centerOffset + (panel.size.width / 2), options.useFractions)}"`;
	const { textWidth, refText } = useTextWidth(centerText.length);

	if (!options.showAllDimensions && !(panelItemGroup?.showCenterDimension ?? true)) return <></>;
	const xRadius = (textWidth / 2) + 1;
	const yRadius = fontSize * 0.85;
	const yBaseSize =
		isCountertop && island.type === IslandType.Corner
			? Math.max(island.panels.getFrontPanel().countertopOverhang, distanceFrontFace) + 4
			: isCountertop
				? Math.max(island.panels.getFrontPanel().countertopOverhang, distanceFrontFace) + 1
				: island.islandSize.height / 2;
	let yStart = DIMENSION_CENTER_LINE_INITIAL_Y + yBaseSize;
	const xEnd = 0;
	const yEnd = yBaseSize + DIMENSION_CENTER_LINE_PADDING_TOP;

	if (panel?.panelItemGroups != null) {
		if (panelItemGroup.hasPassthrough) yStart += 4;
	}

	// Offset oval downward when overlapping with adjacent ovals. Sort passthrough groups AFTER
	// non-passthrough ones so the group that already sits +4 lower (hasPassthrough, above) is the
	// one that receives the additional stagger -- otherwise a random id order can push the vent
	// down onto the passthrough's row and one label hides behind the other. Ties resolve by id
	// for stability.
	const overlapThreshold = (fontSize * 4) + 2;
	const overlapYOffset = (() => {
		if (!panel?.panelItemGroups) return 0;
		const overlapping = panel.panelItemGroups.children.filter((sibling) => {
			if (sibling.id === panelItemGroup.id) return false;
			return Math.abs(centerpoint.x - sibling.center.x) < overlapThreshold;
		});
		if (overlapping.length === 0) return 0;
		const cluster = [...overlapping, panelItemGroup].sort((a, b) => {
			if (a.hasPassthrough !== b.hasPassthrough) return a.hasPassthrough ? 1 : -1;
			return a.id < b.id ? -1 : 1;
		});
		const rank = cluster.findIndex((g) => g.id === panelItemGroup.id);
		return rank % 2 === 1 ? yRadius * 2.5 : 0;
	})();
	yStart += overlapYOffset;

	return (
		<g ref={movableRef} id={panelItemGroupId} {...addContextMenu({ menuKey: ReferenceEntryType.CenterMarker, id: panelItemGroupId })}>
			<ellipse cx={xEnd} cy={yStart} rx={xRadius} ry={yRadius} fill="lightgray" stroke="black" strokeWidth={DIMENSION_CENTER_LINE_OVAL_STROKE_WIDTH} opacity=".7" />

			<line x1={xEnd} y1={yStart - yRadius} x2={xEnd} y2={yEnd + DIMENSION_STROKE_WIDTH} stroke="black" strokeWidth={DIMENSION_STROKE_WIDTH} strokeDasharray=".5 .25" />
			<use xlinkHref="#up-arrow" x={xEnd - halfArrow} y={yEnd} />

			<text
				ref={refText}
				style={{ userSelect: 'none' }}
				textAnchor="middle"
				fontSize={fontSize}
				x={xEnd}
				y={yStart + fontSize / 3}
				className="rta-designer-appliance-group-measure"
			>
				{centerText}
			</text>
			<ellipse cx={xEnd} cy={yStart} rx={xRadius} ry={yRadius} fill="black" strokeWidth={DIMENSION_CENTER_LINE_OVAL_STROKE_WIDTH} opacity="0" />
			{/* <animated.div {...bind()} style={{ x, y }} /> */}
		</g>
	);
};
